import React, { useState, useEffect } from "react";
import { Container, Row, Col, Spinner, Offcanvas, Button } from "react-bootstrap";
import { FaSearch, FaBookOpen, FaArrowUp } from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../components/common/Header";
import NotesSidebar from "../../components/student/NotesSidebar";
import NotesViewer from "../../components/student/NotesViewer";
import StudentNotesService from "../../services/studentNotesService";
import "../../styles/notes-student.css";

const NotesHub = () => {
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredTopics, setFilteredTopics] = useState([]);

  // Navigation state
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [loadingNavigation, setLoadingNavigation] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Load topics on component mount
  useEffect(() => {
    loadTopics();
  }, []);

  // Handle search with recursive filtering
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredTopics(topics);
    } else {
      const searchLower = searchTerm.toLowerCase();

      const searchInStructure = (items) => {
        let results = [];

        items.forEach((course) => {
          const courseMatches = course.course_name
            ?.toLowerCase()
            .includes(searchLower);

          const searchInTopics = (topicItems) => {
            let matchingTopics = [];

            topicItems.forEach((item) => {
              const titleMatches = item.title
                ?.toLowerCase()
                .includes(searchLower);

              let matchingChildren = [];
              if (item.children && item.children.length > 0) {
                matchingChildren = searchInTopics(item.children);
              }

              if (titleMatches || matchingChildren.length > 0) {
                const itemCopy = { ...item };
                if (matchingChildren.length > 0) {
                  itemCopy.children = matchingChildren;
                }
                matchingTopics.push(itemCopy);
              }
            });

            return matchingTopics;
          };

          const matchingStructure = searchInTopics(course.structure || []);

          if (courseMatches || matchingStructure.length > 0) {
            results.push({
              ...course,
              structure:
                matchingStructure.length > 0
                  ? matchingStructure
                  : course.structure,
            });
          }
        });

        return results;
      };

      setFilteredTopics(searchInStructure(topics));
    }
  }, [searchTerm, topics]);

  // Load all topics from API
  const loadTopics = async () => {
    try {
      setLoading(true);
      const response = await StudentNotesService.getTopics();
      const data = response.data || [];
      setTopics(data);
      setFilteredTopics(data);
    } catch (error) {
      console.error("Failed to load topics:", error);
      toast.error("Failed to load notes. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  // Extract all topics from a folder structure (recursive)
  const extractTopicsFromFolder = (structure) => {
    let topicsList = [];

    for (const item of structure) {
      if (item.content_type === "topic" && item.is_published) {
        topicsList.push(item);
      }
      if (item.children && item.children.length > 0) {
        topicsList.push(...extractTopicsFromFolder(item.children));
      }
    }

    return topicsList;
  };

  // Find current folder and build playlist
  const buildPlaylistForTopic = (topic, coursesData) => {
    for (const course of coursesData) {
      const findParentFolder = (items, targetTopicId, parentFolder = null) => {
        for (const item of items) {
          if (item.id === targetTopicId && item.content_type === "topic") {
            return parentFolder;
          }
          if (item.children && item.children.length > 0) {
            const found = findParentFolder(
              item.children,
              targetTopicId,
              item.content_type === "folder" ? item : parentFolder,
            );
            if (found !== null) return found;
          }
        }
        return null;
      };

      const parentFolder = findParentFolder(course.structure, topic.id);

      if (parentFolder !== null) {
        const allTopics = extractTopicsFromFolder([parentFolder]);
        allTopics.sort(
          (a, b) => (a.display_order || 0) - (b.display_order || 0),
        );
        return allTopics;
      }
    }
    return [topic];
  };

  // Handle topic selection
  const handleTopicSelect = async (topic) => {
    setSelectedTopic(topic);
    setLoadingContent(true);

    try {
      const playlistData = buildPlaylistForTopic(topic, filteredTopics);
      setPlaylist(playlistData);

      const currentIdx = playlistData.findIndex((t) => t.id === topic.id);
      setCurrentIndex(currentIdx >= 0 ? currentIdx + 1 : 1);

      const response = await StudentNotesService.getTopic(topic.slug);
      setBlocks(response.data.blocks || []);
      setExercises(response.data.exercises || []);

      // Ensure we start at the top of the new topic
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Failed to load topic content:", error);
      toast.error("Failed to load topic content");
    } finally {
      setLoadingContent(false);
    }
  };

  // Navigate to previous topic
  const handlePreviousTopic = async () => {
    if (!playlist.length) return;

    const prevIndex = currentIndex - 2;
    if (prevIndex >= 0) {
      const prevTopic = playlist[prevIndex];
      if (prevTopic) {
        setLoadingNavigation(true);
        try {
          setSelectedTopic(prevTopic);
          setCurrentIndex(prevIndex + 1);

          const response = await StudentNotesService.getTopic(prevTopic.slug);
          setBlocks(response.data.blocks || []);
          setExercises(response.data.exercises || []);

          window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (error) {
          console.error("Failed to load previous topic:", error);
          toast.error("Failed to load previous topic");
        } finally {
          setLoadingNavigation(false);
        }
      }
    }
  };

  // Navigate to next topic
  const handleNextTopic = async () => {
    if (!playlist.length) return;

    const nextIndex = currentIndex;
    if (nextIndex < playlist.length) {
      const nextTopic = playlist[nextIndex];
      if (nextTopic) {
        setLoadingNavigation(true);
        try {
          setSelectedTopic(nextTopic);
          setCurrentIndex(nextIndex + 1);

          const response = await StudentNotesService.getTopic(nextTopic.slug);
          setBlocks(response.data.blocks || []);
          setExercises(response.data.exercises || []);

          window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (error) {
          console.error("Failed to load next topic:", error);
          toast.error("Failed to load next topic");
        } finally {
          setLoadingNavigation(false);
        }
      }
    }
  };

  // Handle folder selection
  const handleFolderSelect = (folder) => {
    setSelectedTopic(null);
    setBlocks([]);
    setExercises([]);
    setPlaylist([]);
    setCurrentIndex(-1);
  };

  // Handle mark complete/incomplete — updates tree in-place so sidebar stays open
  const handleMarkComplete = async () => {
    if (!selectedTopic) return;

    try {
      if (!selectedTopic.is_completed) {
        await StudentNotesService.markCompleted(selectedTopic.id);
        toast.success("Topic marked as completed! 🎉");
      } else {
        await StudentNotesService.markIncomplete(selectedTopic.id);
        toast.info("Topic marked as incomplete");
      }

      const newStatus = !selectedTopic.is_completed;

      // Update selected topic state
      setSelectedTopic((prev) => ({
        ...prev,
        is_completed: newStatus,
      }));

      // Update playlist completion status
      setPlaylist((prev) =>
        prev.map((topic) =>
          topic.id === selectedTopic.id
            ? { ...topic, is_completed: newStatus }
            : topic,
        ),
      );

      // Update completion status in the topic tree in-place (no full reload)
      const updateTreeCompletion = (structure) => {
        return structure.map((item) => {
          if (item.id === selectedTopic.id) {
            return { ...item, is_completed: newStatus };
          }
          if (item.children && item.children.length > 0) {
            return { ...item, children: updateTreeCompletion(item.children) };
          }
          return item;
        });
      };

      setTopics((prev) =>
        prev.map((course) => ({
          ...course,
          structure: updateTreeCompletion(course.structure || []),
        })),
      );
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Failed to update status. Please try again.");
    }
  };

  // Check if navigation is available
  const hasPrevious = currentIndex > 1;
  const hasNext = currentIndex < playlist.length;

  // Loading state
  if (loading) {
    return (
      <div>
        <Header />
        <div className="notes-loading">
          <Spinner animation="border" variant="primary" />
          <p>Loading your notes library...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notes-hub-page">
      <Header />

      {/* Minimal Header Section */}
      <div className="notes-header-minimal">
        <Container>
          <div className="notes-header-content">
            <div className="notes-header-icon">
              <FaBookOpen />
            </div>
            <div>
              <h1 className="notes-header-title">Notes Library</h1>
              <p className="notes-header-subtitle">
                Access all your course materials, code examples, and practice
                exercises
              </p>
            </div>
          </div>

          {/* Search Bar & Mobile Toggle */}
          <div className="d-flex align-items-center gap-2 mt-3 mt-lg-0">
            <div className="notes-search-wrapper-minimal flex-grow-1">
              <FaSearch className="notes-search-icon-minimal" />
              <input
                type="text"
                className="notes-search-input-minimal"
                placeholder="Search notes by title or course name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button 
              variant="primary" 
              className="d-lg-none text-nowrap rounded-3 shadow-sm"
              onClick={() => setShowMobileSidebar(true)}
            >
              <FaBookOpen className="me-2" /> Library
            </Button>
          </div>
        </Container>
      </div>

      {/* Mobile Sidebar Offcanvas */}
      <Offcanvas show={showMobileSidebar} onHide={() => setShowMobileSidebar(false)} placement="start">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="fw-bold">Notes Library</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          <NotesSidebar
            courses={filteredTopics}
            selectedTopicId={selectedTopic?.id}
            onTopicSelect={(topic) => {
              handleTopicSelect(topic);
              setShowMobileSidebar(false);
            }}
            onFolderSelect={handleFolderSelect}
          />
        </Offcanvas.Body>
      </Offcanvas>

      <Container className="notes-hub-container">
        <Row className="g-4">
          <Col
            lg={4}
            xl={3}
            className="notes-sidebar-column d-none d-lg-block"
            style={{ position: "sticky", top: "80px", alignSelf: "flex-start", zIndex: 10 }}
          >
            <NotesSidebar
              courses={filteredTopics}
              selectedTopicId={selectedTopic?.id}
              onTopicSelect={handleTopicSelect}
              onFolderSelect={handleFolderSelect}
            />
          </Col>

          <Col lg={8} xl={9}>
            {loadingContent || loadingNavigation ? (
              <div className="notes-content-loading">
                <Spinner animation="border" variant="primary" />
                <p>Loading content...</p>
              </div>
            ) : selectedTopic ? (
              <NotesViewer
                topic={selectedTopic}
                blocks={blocks}
                exercises={exercises}
                onMarkComplete={handleMarkComplete}
                isCompleted={selectedTopic.is_completed}
                // Navigation props
                hasPrevious={hasPrevious}
                hasNext={hasNext}
                onPrevious={handlePreviousTopic}
                onNext={handleNextTopic}
                currentIndex={currentIndex}
                totalTopics={playlist.length}
              />
            ) : (
              <div className="notes-empty-state">
                <div className="notes-empty-icon">📖</div>
                <h3>Select a topic to start learning</h3>
                <p>
                  Choose a topic from the sidebar to view notes, code examples,
                  and practice exercises.
                </p>
                {searchTerm && filteredTopics.length === 0 && (
                  <p className="text-muted mt-3">
                    No results found for "{searchTerm}". Try a different search
                    term.
                  </p>
                )}
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <Button
          variant="primary"
          className="rounded-circle shadow"
          style={{
            position: 'fixed',
            bottom: '80px',
            right: '20px',
            width: '50px',
            height: '50px',
            zIndex: 999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <FaArrowUp />
        </Button>
      )}
    </div>
  );
};

export default NotesHub;
